import { UAParser } from 'ua-parser-js';
import logger from './logger';

/**
 * Device information utilities for detecting OS, browser, and user agent details
 * Based on NetSfere's implementation pattern with fallbacks
 */

/**
 * Get user agent string
 * @returns {string} Full user agent string
 */
export const getUserAgent = () => {
  return navigator.userAgent || 'Unknown';
};

/**
 * Cache for UAParser result to avoid re-parsing user agent on every call
 * Significant performance improvement for repeated calls
 */
let cachedParserResult = null;

/**
 * Get parsed result from UAParser (cached at module level)
 * @returns {object} Parsed user agent result
 */
const getParserResult = () => {
  // Return cached result if available
  if (cachedParserResult !== null) {
    return cachedParserResult;
  }

  // Parse user agent and cache result
  try {
    const parser = new UAParser(getUserAgent());
    cachedParserResult = parser.getResult();
    return cachedParserResult;
  } catch (error) {
    logger.warn('UAParser failed:', error);
    // Cache null to prevent repeated parsing attempts on error
    cachedParserResult = null;
    return null;
  }
};

/**
 * Get operating system name
 * @returns {string} OS name (e.g., "macOS", "Windows", "Linux")
 */
export const getOSName = () => {
  try {
    const result = getParserResult();
    if (result && result.os && result.os.name) {
      const osName = result.os.name;
      // Normalize OS names
      if (osName.toLowerCase().includes('mac')) {
        return 'macOS';
      }
      return osName;
    }
  } catch (error) {
    logger.warn('Failed to get OS name:', error);
  }

  // Fallback using navigator
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'macOS';
  if (ua.includes('win')) return 'Windows';
  if (ua.includes('linux')) return 'Linux';
  if (ua.includes('android')) return 'Android';
  if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';

  return 'Unknown';
};

/**
 * Get operating system version
 * @returns {string} OS version (e.g., "14.0", "10")
 */
export const getOSVersion = () => {
  try {
    const result = getParserResult();
    if (result && result.os && result.os.version) {
      return result.os.version;
    }
  } catch (error) {
    logger.warn('Failed to get OS version:', error);
  }

  return '';
};

/**
 * Get full OS version string
 * @returns {string} OS name and version (e.g., "macOS 14.0", "Windows 10")
 */
export const getOSVersionString = () => {
  const osName = getOSName();
  const osVersion = getOSVersion();

  if (osVersion) {
    return `${osName} ${osVersion}`;
  }

  return osName;
};

/**
 * Get browser name
 * @returns {string} Browser name (e.g., "Chrome", "Firefox", "Safari")
 */
export const getBrowserName = () => {
  try {
    const result = getParserResult();
    if (result && result.browser && result.browser.name) {
      return result.browser.name;
    }
  } catch (error) {
    logger.warn('Failed to get browser name:', error);
  }

  return 'Unknown';
};

/**
 * Get browser version
 * @returns {string} Browser version
 */
export const getBrowserVersion = () => {
  try {
    const result = getParserResult();
    if (result && result.browser && result.browser.version) {
      return result.browser.version;
    }
  } catch (error) {
    logger.warn('Failed to get browser version:', error);
  }

  return '';
};

/**
 * Get full browser version string
 * @returns {string} Browser name and version (e.g., "Chrome 120.0")
 */
export const getBrowserVersionString = () => {
  const browserName = getBrowserName();
  const browserVersion = getBrowserVersion();

  if (browserVersion) {
    return `${browserName} ${browserVersion}`;
  }

  return browserName;
};

/**
 * Platform detection helpers
 */
export const isWindows = () => {
  return getOSName().toLowerCase().includes('windows');
};

export const isMac = () => {
  return getOSName().toLowerCase().includes('mac');
};

export const isLinux = () => {
  return getOSName().toLowerCase().includes('linux');
};

export const isAndroid = () => {
  return getOSName().toLowerCase() === 'android';
};

export const isiOS = () => {
  return getOSName().toLowerCase() === 'ios';
};
